import { type ReactNode } from 'react';
import { cn } from '../../utils/cn';

export interface AppShellProps {
  readonly top?: ReactNode;
  readonly side?: ReactNode;
  readonly children: ReactNode;
  readonly className?: string;
}

/**
 * App shell — sticky top bar + optional side nav + main content area.
 *
 *   <AppShell top={<TopNav />} side={<SideNav />}>
 *     <main>page content</main>
 *   </AppShell>
 *
 * Side nav collapses to nothing on small viewports; the consumer is expected
 * to also render a Drawer / BottomTabBar for mobile nav.
 */
export function AppShell({ top, side, children, className }: AppShellProps) {
  return (
    // Fixed app-frame: the shell fills the viewport and never scrolls itself —
    // the top bar + side nav stay pinned while ONLY <main> scrolls. `min-h-0`
    // on the row lets the flex children shrink so their own overflow can work.
    <div
      className={cn(
        'h-screen supports-[height:100dvh]:h-dvh flex flex-col overflow-hidden bg-bg text-ink',
        className,
      )}
    >
      {top ? (
        <header className="shrink-0 z-30 bg-bg/90 backdrop-blur-md border-b border-hairline">
          {top}
        </header>
      ) : null}
      <div className="flex-1 min-h-0 flex">
        {side ? (
          <aside className="hidden md:flex w-60 shrink-0 border-e border-hairline overflow-y-auto">
            {side}
          </aside>
        ) : null}
        <main className="flex-1 min-w-0 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}
